// external dependencies
import { useEffect, useLayoutEffect } from "react";
import { useState } from "react";
import { useRef } from "react";
import { Tooltip } from "react-tooltip";
import { ReactSVGPanZoom, TOOL_AUTO } from "react-svg-pan-zoom";
import ReactGA from "react-ga4";

// internal component dependencies
import { CourseData, HeatmapArea } from "../datastructures/coursedata";
import { CourseNode } from "./coursenode";
import { CourseNodeCluster } from "./coursenodeCluster";
import { IConnector, ICourseNode, Vector2 } from "../datastructures/dataviz";
import HelpModal from "./help-modal";
import { ControlButton } from "./control-button";
import { PatternLines, PatternCircles } from "@visx/pattern";
import { Connector } from "./connector";
import Legend, { ECELogo } from "./legend";

// internal utility functions
import {
  InitializeCoursemapDatabase,
  InitializeCoursemapEdges,
  InitializeHeatmapAreas,
  InitializeSearchBarList,
} from "../dataparsers/coursemapParser";
import { ProcessCourseDataMapToNodes } from "../dataparsers/courseDataToNode";
import {
  AreaLayoutCore,
  GetClusterCenters,
} from "../database/manualLayoutData";
import {
  extractCoreAngleArray,
  GetKernelsOfArea,
  GetShiftAffectedCourseHomeAreas,
  GridLayoutCourseHeatmap,
  pl,
  RadialLayoutCourseNodes,
  ReinjectCoursesAfterClusterClose,
  ShiftCourseHomeAreas,
  UpdateCourseEdgeVisibility,
  UpdateCourseNodeVisibility,
} from "../utils/nodeLayoutUtils";
import { StyleByHeatmapArea } from "../utils/heatmapUtils";
import { useWindowSize } from "@react-hook/window-size";
import { CoreCourseToFullSemesterString } from "../database/core-course-data";

// internal styles
import Gradients from "../utils/gradients";
import "../styles/coursemap.css";
import "../styles/heatmap.css";

export type ICourseMapProps = {
  width: number;
  height: number;
  // focusedNode: ICourseNode | null
  SetFocusedNode: any;
  SetLoadCompleted: any;
  LoadCompleted: boolean;
  ViewerRendered: boolean;
  SetViewerRendered: any;
};

const areas = [0, 1, 2, 3, 4, 5, 6, 7, 8];

export const CourseMap = ({
  width,
  height,
  SetFocusedNode,
  SetLoadCompleted,
  LoadCompleted,
  SetViewerRendered,
  ViewerRendered,
}: ICourseMapProps) => {
  const [CourseDataMap, SetCourseDataMap] = useState<CourseData[]>([]);
  const [CourseNodes, SetCourseNodes] = useState<ICourseNode[]>([]);
  const [CourseEdges, SetCourseEdges] = useState<IConnector[]>([]);
  const [CourseClusterCenterArray, SetCourseClusterCenterArray] =
    useState<Vector2[]>(AreaLayoutCore);
  const [CourseClusterExpansion, SetCourseClusterExpansion] = useState<
    boolean[]
  >(Array<boolean>(8).fill(false));
  const [CourseClusterFoci, SetCourseClusterFoci] = useState<ICourseNode[]>();
  const [CourseClusterHasFocus, SetCourseClusterHasFocus] = useState<boolean[]>(
    Array<boolean>(8).fill(false)
  );

  const [AnimationsEnable, SetAnimationsEnable] = useState<boolean>(true);
  const [ModalOpen, SetModalOpen] = useState<boolean>(false);
  const [HelpModalOpen, SetHelpModalOpen] = useState<boolean>(false);

  const [HeatmapAreas, SetHeatmapAreas] = useState<HeatmapArea[]>([]);
  const [FocusedHeatmapArea, SetFocusedHeatmapArea] = useState<HeatmapArea>();
  const [SearchBarList, SetSearchBarList] = useState<string[]>();
  const [SearchBarValue, SetSearchBarValue] = useState<string>();
  const [SearchBarVisible, SetSearchBarVisible] = useState<boolean>(false);

  const [isSafari, setIsSafari] = useState<boolean>(false);
  const [coreAngleArray, setCoreAngleArray] = useState<number[]>([
    0, 0, 0, 0, 0,
  ]);

  const [rwidth, rheight] = useWindowSize({
    initialWidth: 400,
    initialHeight: 400,
  });
  const [showNodeTTip, setShowNodeTTip] = useState<boolean>(false);
  const [nodeTTipLoc, setNodeTTipLoc] = useState<Vector2>({ x: 0, y: 0 });
  const [nodeTTipString, setNodeTTipString] = useState<String>("");

  const [value, setValue] = useState(() => {
    const heightOfNormalizedZoom = 2000;
    let new_value: any = {};
    new_value.a = window.innerHeight / heightOfNormalizedZoom;
    new_value.d = window.innerHeight / heightOfNormalizedZoom;
    new_value.e = window.innerWidth / 2;
    new_value.f = window.innerHeight / 2;
    new_value.b = 0;
    new_value.c = 0;
    return new_value;
  });

  const [tool, setTool] = useState(TOOL_AUTO);
  const Viewer = useRef<any>(null);
  const initRef = useRef<boolean>(false);
  const viewerInitRef = useRef<boolean>(false);
  const edgesInitRef = useRef<boolean>(false);
  // refs to hold latest values for use in effects that we intentionally keep minimal deps for
  const CourseNodesRef = useRef<ICourseNode[]>(CourseNodes);
  const CourseEdgesRef = useRef<IConnector[]>(CourseEdges);
  const CourseClusterExpansionRef = useRef<boolean[]>(CourseClusterExpansion);
  const CourseClusterFociRef = useRef<ICourseNode[] | undefined>(CourseClusterFoci);
  const CourseClusterCenterArrayRef = useRef<Vector2[]>(CourseClusterCenterArray);
  const LoadCompletedRef = useRef<boolean>(LoadCompleted);
  const valueRef = useRef<any>(value);
  const SetFocusedNodeRef = useRef<any>(SetFocusedNode);

  const ZoomToCentre = (zoomScale = 1) => {
    //heatmap mode variable zoom

    const heightOfNormalizedZoom = 2000 * zoomScale;

    var new_value = { ...value };
    new_value.a = window.innerHeight / heightOfNormalizedZoom;
    new_value.d = window.innerHeight / heightOfNormalizedZoom;
    new_value.e = window.innerWidth / 2;
    new_value.f = window.innerHeight / 2;

    setValue(new_value);
  };

  const ZoomToArea = (area: number) => {
    const heightOfNormalizedZoom = 2000;

    var offset_factor = rheight / heightOfNormalizedZoom; // 1000/1200 = 0.6
    var new_value = { ...value };
    new_value.a = window.innerHeight / heightOfNormalizedZoom;
    new_value.d = window.innerHeight / heightOfNormalizedZoom;

    new_value.e =
      window.innerWidth / 2 - CourseClusterCenterArray[area].x * offset_factor;
    new_value.f =
      window.innerHeight / 2 - CourseClusterCenterArray[area].y * offset_factor;

    setValue(new_value);
  };

  const ZoomToCourse = (CourseNode: ICourseNode) => {
    if (CourseNode === undefined) return;
    if (FocusedHeatmapArea !== undefined) {
      SetFocusedNode(CourseNode);
      return;
    } //don't zoom & expand if heatmap is active

    const UpdatedClusterHighlight = [...CourseClusterExpansion];
    UpdatedClusterHighlight[CourseNode.homeArea] = true;
    SetCourseClusterExpansion(UpdatedClusterHighlight);
    SetCourseClusterCenterArray(GetClusterCenters(UpdatedClusterHighlight));

    ZoomToArea(CourseNode.homeArea);

    const UpdatedClusterFoci = CourseClusterFoci
      ? [...CourseClusterFoci]
      : Array(CourseClusterExpansion.length).fill(undefined as any);
    UpdatedClusterFoci[CourseNode.homeArea] = CourseNode;
    SetCourseClusterFoci([...UpdatedClusterFoci]);

    var UpdatedCourseNodes = RadialLayoutCourseNodes(
      CourseNodes,
      CourseClusterCenterArray[CourseNode.homeArea],
      CourseNode.homeArea,
      UpdatedClusterFoci[CourseNode.homeArea]
    );
    SetCourseNodes([...UpdatedCourseNodes]);
    SetCourseNodes(UpdateCourseNodeVisibility(CourseNodes, UpdatedClusterHighlight));

    if (CourseNode.homeArea === 0)
      setCoreAngleArray(extractCoreAngleArray(CourseNodes, UpdatedClusterFoci[0]));

    // console.dir(CourseNode)
    SetFocusedNode(JSON.parse(JSON.stringify(CourseNode)));
  };

  const ZoomAndOpenArea = (area: number) => {
    const UpdatedClusterHighlight = [...CourseClusterExpansion];
    UpdatedClusterHighlight[area] = true;
    SetCourseClusterExpansion(UpdatedClusterHighlight);
    SetCourseClusterCenterArray(GetClusterCenters(UpdatedClusterHighlight));
    SetCourseNodes([...ShiftCourseHomeAreas(CourseNodes, area)]);
    var UpdatedCourseNodes = RadialLayoutCourseNodes(
      CourseNodes,
      CourseClusterCenterArray[area],
      area,
      CourseClusterFoci ? CourseClusterFoci[area] : (undefined as any)
    );
    SetCourseNodes([...UpdatedCourseNodes]);

    SetCourseNodes(
      UpdateCourseNodeVisibility(CourseNodes, UpdatedClusterHighlight)
    );
    ZoomToArea(area);

    // change layout of opened area as courses may have been stolen
    for (var a = 0; a < CourseClusterExpansion.length; a++) {
      if (CourseClusterExpansion[a]) {
        UpdatedCourseNodes = RadialLayoutCourseNodes(
          UpdatedCourseNodes,
          CourseClusterCenterArray[a],
          a,
          CourseClusterFoci![a]
        );
        SetCourseNodes(
          UpdateCourseNodeVisibility(
            UpdatedCourseNodes,
            UpdatedClusterHighlight
          )
        );
      }
    }
  };

  const toggleModal = () => {
    // alert("clicked")
    var modal = document.getElementById("focus-area-selection-modal");
    modal!.classList.toggle("opacity-0");
    modal!.classList.toggle("pointer-events-none");
    // console.log('modal opened')
    SetModalOpen(!ModalOpen);
  };

  const toggleHelpModal = () => {
    // alert("help modal opened")
    var modal = document.getElementById("help-submenu-modal");
    modal!.classList.toggle("opacity-0");
    modal!.classList.toggle("pointer-events-none");
    // console.log('modal opened')
    SetHelpModalOpen(!HelpModalOpen);
  };

  interface ICourseClusterToggle {
    area: number;
  }

  const toggleCourseCluster = (area: ICourseClusterToggle["area"]) => {
    // set expansion
    const UpdatedClusterHighlight = [...CourseClusterExpansion];
    UpdatedClusterHighlight[area] = !UpdatedClusterHighlight[area];
    SetCourseClusterExpansion(UpdatedClusterHighlight);
    SetCourseClusterCenterArray(GetClusterCenters(UpdatedClusterHighlight));

    //analytics
    ReactGA.event({
      category: "User",
      action: `Toggled Area Cluster ${area}`,
    });

    var perturbedAreas = GetShiftAffectedCourseHomeAreas(CourseNodes, area);

    //Update home areas (eg moving multi area courses into the most recently opened course)
    SetCourseNodes([...ShiftCourseHomeAreas(CourseNodes, area)]);

    // React bails out of rerender if the array looks the same, even if the values are changed
    // Here I'm creating a new array to force rerender
    var UpdatedCourseNodes = [...CourseNodes];
    RadialLayoutCourseNodes(
      CourseNodes,
      CourseClusterCenterArray[area],
      area,
      CourseClusterFoci ? CourseClusterFoci[area] : (undefined as any)
    );

    for (var a = 0; a < perturbedAreas.length; a++) {
      UpdatedCourseNodes = RadialLayoutCourseNodes(
        UpdatedCourseNodes,
        CourseClusterCenterArray[perturbedAreas[a]],
        perturbedAreas[a],
        CourseClusterFoci ? CourseClusterFoci[perturbedAreas[a]] : (undefined as any)
      );
    }
    SetCourseNodes([...UpdatedCourseNodes]);
    SetCourseNodes(
      UpdateCourseNodeVisibility(CourseNodes, UpdatedClusterHighlight)
    );

    //on show
    if (UpdatedClusterHighlight[area] === true) {
      ZoomAndOpenArea(area);
      SetFocusedNode(null);
      if (area === 0)
        setCoreAngleArray(
          extractCoreAngleArray(CourseNodes, CourseClusterFoci ? CourseClusterFoci[0] : (undefined as any))
        );

      // change layout of opened area as courses may have been stolen
      for (a = 0; a < CourseClusterExpansion.length; a++) {
        if (CourseClusterExpansion[a]) {
          UpdatedCourseNodes = RadialLayoutCourseNodes(
            UpdatedCourseNodes,
            CourseClusterCenterArray[a],
            a,
            CourseClusterFoci ? CourseClusterFoci[a] : (undefined as any)
          );
          SetCourseNodes(
            UpdateCourseNodeVisibility(
              UpdatedCourseNodes,
              UpdatedClusterHighlight
            )
          );
        }
      }
    }
    //on hide
    else {
      SetFocusedNode(null);
      //clear focus to rerender inner text
      const UpdatedClusterHasFocus = [...CourseClusterHasFocus];
      UpdatedClusterHasFocus[area] = false;
      SetCourseClusterHasFocus([...UpdatedClusterHasFocus]);
      // reinject other core course areas and relayout
      SetCourseNodes([
        ...ReinjectCoursesAfterClusterClose(
          CourseNodes,
          CourseClusterExpansion
        ),
      ]);
      // reorient all other areas
      UpdatedCourseNodes = [...CourseNodes];
      // change layout of opened area as courses may have been stolen
      for (a = 0; a < CourseClusterExpansion.length; a++) {
        if (CourseClusterExpansion[a]) {
          UpdatedCourseNodes = RadialLayoutCourseNodes(
            UpdatedCourseNodes,
            CourseClusterCenterArray[a],
            a,
            CourseClusterFoci ? CourseClusterFoci[a] : (undefined as any)
          );
          SetCourseNodes(
            UpdateCourseNodeVisibility(
              UpdatedCourseNodes,
              UpdatedClusterHighlight
            )
          );
        }
      }
      ZoomToCentre();
      // console.log(`Hiding Area ${area}`)
    }
  };

  const processSearch = (e) => {
    if (e.key === "Enter") {
      //analytics testing
      ReactGA.event({
        category: "User",
        action: `Searched in searchbar for ${SearchBarValue}`,
      });

      var targetedCourse = CourseNodes.find(
        (el) =>
          el.data.courseCode.toLocaleLowerCase() ===
          SearchBarValue?.split(" - ")[0].toLocaleLowerCase()
      );
      if (targetedCourse === undefined || targetedCourse === null) {
        alert(
          `Sorry, ${SearchBarValue?.split(
            " - "
          )[0].toLocaleLowerCase()} was not found.`
        );
      } else {
        // SetFocusedNode(targetedCourse)
        ZoomToCourse(targetedCourse);
      }
    }
  };

  const toggleCourseNode = (el) => {
    SetFocusedNode(el);
    //analytics testing
    ReactGA.event({
      category: "User",
      action: `Toggled course node ${el.data.courseCode}`,
    });
    if (FocusedHeatmapArea === undefined) {
      const UpdatedClusterFoci = CourseClusterFoci
        ? [...CourseClusterFoci]
        : Array(CourseClusterExpansion.length).fill(undefined as any);
      UpdatedClusterFoci[el.homeArea] = el;
      SetCourseClusterFoci([...UpdatedClusterFoci]);
      let UpdatedCourseNodes = RadialLayoutCourseNodes(
        CourseNodes,
        GetClusterCenters(CourseClusterExpansion)[el.homeArea],
        el.homeArea,
        UpdatedClusterFoci[el.homeArea]
      );
      SetCourseNodes([...UpdatedCourseNodes]);
      const UpdatedClusterHasFocus = [...CourseClusterHasFocus];
      UpdatedClusterHasFocus[el.homeArea] = true;
      SetCourseClusterHasFocus([...UpdatedClusterHasFocus]);
      // iff we are modifying an area 0 node, we modify the core angle array, otherwise don't bother.
      if (el.homeArea === 0)
        setCoreAngleArray(extractCoreAngleArray(CourseNodes, UpdatedClusterFoci[0]));
    }
  };

  const GenerateCoursenodeTooltipString = (el: ICourseNode) => {
    var ret = "";

    ret += el.data.courseCode + " - " + el.data.courseName;
    if (el.homeArea === 0) {
      ret += "\n" + CoreCourseToFullSemesterString(el.data.courseCode);
    }
    return ret;
  };

  async function getCourseMapDatabaseAsync() {
    const db = await InitializeCoursemapDatabase();
    console.debug("CourseMap: InitializeCoursemapDatabase returned", db?.length);
    SetCourseDataMap(db);
    const heat = await InitializeHeatmapAreas();
    console.debug("CourseMap: InitializeHeatmapAreas returned", heat?.length);
    SetHeatmapAreas(heat);
  }

  // analytics setup
  useEffect(() => {
    ReactGA.initialize("G-YJKM53JN29");
    ReactGA.send("pageview");
  }, []);

  // Initial data acquisiton
  useEffect(() => {
    getCourseMapDatabaseAsync();
  }, []);

  //Safari browser check compatibility
  useEffect(() => {
    setIsSafari(isSafari);
  }, [isSafari]);

  // Course node initialization when data is loaded
  useEffect(() => {
    const nodes = ProcessCourseDataMapToNodes(CourseDataMap);
    console.debug("CourseMap: ProcessCourseDataMapToNodes created", nodes?.length, "nodes");
    SetCourseNodes(nodes);
  }, [CourseDataMap]);

  // keep refs in sync with latest state to avoid staleness in effects
  useEffect(() => {
    CourseNodesRef.current = CourseNodes;
  }, [CourseNodes]);
  useEffect(() => {
    CourseEdgesRef.current = CourseEdges;
  }, [CourseEdges]);
  useEffect(() => {
    CourseClusterExpansionRef.current = CourseClusterExpansion;
  }, [CourseClusterExpansion]);
  useEffect(() => {
    CourseClusterFociRef.current = CourseClusterFoci;
  }, [CourseClusterFoci]);
  useEffect(() => {
    CourseClusterCenterArrayRef.current = CourseClusterCenterArray;
  }, [CourseClusterCenterArray]);
  useEffect(() => {
    LoadCompletedRef.current = LoadCompleted;
  }, [LoadCompleted]);
  useEffect(() => {
    valueRef.current = value;
  }, [value]);
  useEffect(() => {
    SetFocusedNodeRef.current = SetFocusedNode;
  }, [SetFocusedNode]);

  // Adjust edges when course nodes modified
  useEffect(() => {
    //initialization
    if (CourseNodes.length > 0) {
      if (!edgesInitRef.current) {
        edgesInitRef.current = true;
        let initialEdges = InitializeCoursemapEdges(CourseNodes);
        let UpdatedCourseEdges = UpdateCourseEdgeVisibility(
          initialEdges,
          CourseNodes,
          CourseClusterFoci
        );
        SetCourseEdges([...UpdatedCourseEdges]);
      } else {
        // Update existing edges based on current nodes and foci
        let UpdatedCourseEdges = UpdateCourseEdgeVisibility(
          CourseEdgesRef.current,
          CourseNodes,
          CourseClusterFoci
        );
        SetCourseEdges([...UpdatedCourseEdges]);
      }
    } else {
      // preinitialization - reset everything
      edgesInitRef.current = false;
      SetCourseEdges([]);
    }
  }, [CourseClusterFoci, CourseNodes]);

  // Initialize Course clusters
  useEffect(() => {
    // Run initialization once when CourseNodes become available.
    if (CourseNodes.length !== 0 && !initRef.current) {
      initRef.current = true;
      let InitKernels: ICourseNode[] = [];
      for (let i = 0; i <= 8; i++) {
        let kernels = GetKernelsOfArea(CourseNodes, i);
        let selectedKernel =
          kernels[Math.floor(Math.random() * kernels.length)];
        // var UpdatedCourseNodes = RadialLayoutCourseNodes(CourseNodes, AreaLayout[i], i, selectedKernel)
        InitKernels.push(selectedKernel);
      }
      SetCourseClusterFoci([]);
      SetCourseClusterCenterArray(GetClusterCenters(CourseClusterExpansion));

      //initialize searchbar course code list:
      SetSearchBarList(InitializeSearchBarList(CourseDataMap));

      console.debug("CourseMap: finishing initialization, setting LoadCompleted=true");
      SetLoadCompleted(true);
    }
  }, [CourseNodes, CourseClusterExpansion, CourseDataMap, SetLoadCompleted]);

  // Heatmap Activation
  // Heatmap Activation (uses refs to avoid dependency-induced re-renders)
  useEffect(() => {
    const nodes = CourseNodesRef.current;
    const clusterExpansion = CourseClusterExpansionRef.current;
    const clusterFoci = CourseClusterFociRef.current;
    const clusterCenters = CourseClusterCenterArrayRef.current;
    const currentValue = valueRef.current;
    const setFocusedNode = SetFocusedNodeRef.current;

    if (FocusedHeatmapArea === undefined) {
      // exit heatmap: center view
      const heightOfNormalizedZoom = 2000;
      const new_value: any = { ...currentValue };
      new_value.a = window.innerHeight / heightOfNormalizedZoom;
      new_value.d = window.innerHeight / heightOfNormalizedZoom;
      new_value.e = window.innerWidth / 2;
      new_value.f = window.innerHeight / 2;
      setValue(new_value);

      let zoom = document.getElementById("zoom-bg");
      zoom?.classList.remove("heatmap-bg");

      // compute updated nodes once and set state once
      let updatedNodes = StyleByHeatmapArea({ CourseNodes: nodes, area: -1 });
      if (LoadCompletedRef.current) {
        const newExpansion = Array(clusterExpansion.length).fill(false);
        SetCourseClusterExpansion(newExpansion);
        SetCourseClusterCenterArray(GetClusterCenters(newExpansion));

        for (let area = 0; area < newExpansion.length; area++) {
          updatedNodes = RadialLayoutCourseNodes(
            updatedNodes,
            clusterCenters[area],
            area,
            (clusterFoci ? (clusterFoci[area] as ICourseNode) : (undefined as any))
          );
        }
        updatedNodes = UpdateCourseNodeVisibility(updatedNodes, newExpansion);
      }
      SetCourseNodes(updatedNodes);
    } else {
      // entry into heatmap mode
      setFocusedNode(null);
      // ZoomToCentre(0.6) inline
      const heightOfNormalizedZoom = 2000 * 0.6;
      const new_value: any = { ...currentValue };
      new_value.a = window.innerHeight / heightOfNormalizedZoom;
      new_value.d = window.innerHeight / heightOfNormalizedZoom;
      new_value.e = window.innerWidth / 2;
      new_value.f = window.innerHeight / 2;
      setValue(new_value);

      let zoom = document.getElementById("zoom-bg");
      zoom?.classList.add("heatmap-bg");

      const newExpansion = Array(clusterExpansion.length).fill(true);
      SetCourseClusterExpansion(newExpansion);
      SetCourseClusterCenterArray(GetClusterCenters(newExpansion));

      // compute nodes through the pipeline and set once
      let updatedNodes = UpdateCourseNodeVisibility(nodes, newExpansion);
      updatedNodes = StyleByHeatmapArea({ CourseNodes: updatedNodes, area: FocusedHeatmapArea.areaIndex });
      const gridLayout = GridLayoutCourseHeatmap(updatedNodes, FocusedHeatmapArea);
      SetCourseNodes(gridLayout);
      pl("Grid Layout", gridLayout);
      pl("CourseNodes", gridLayout);

      // reset edges, since we just severed them:
      const initialEdges = InitializeCoursemapEdges(gridLayout);
      const UpdatedCourseEdges = UpdateCourseEdgeVisibility(initialEdges, gridLayout, clusterFoci);
      SetCourseEdges([...UpdatedCourseEdges]);
    }
  }, [FocusedHeatmapArea, LoadCompleted]);

  // Initial Zoom Setting using these two useEffects
  useEffect(() => {
    if (viewerInitRef.current) return;
    if (ViewerRendered) return;
    const currentValue = valueRef.current;
    // only run when viewer is idle
    if (currentValue?.mode === "idle") {
      // inline ZoomToCentre
      const heightOfNormalizedZoom = 2000;
      const new_value: any = { ...currentValue };
      new_value.a = window.innerHeight / heightOfNormalizedZoom;
      new_value.d = window.innerHeight / heightOfNormalizedZoom;
      new_value.e = window.innerWidth / 2;
      new_value.f = window.innerHeight / 2;
      setValue(new_value);

      // mark viewer as rendered once we've set initial view
      SetViewerRendered(true); //at this point, the SVG Pan Zoom has rendered as well
      viewerInitRef.current = true;
    }
  }, [ViewerRendered, SetViewerRendered]);

  // Toggle animations for accessibility (ie. CSS Transition)
  useEffect(() => {
    var animated_elements = document.querySelectorAll(
      ".node-base,.edge_base,.circle-animated,.dashed-outline-flow"
    );
    // console.log(`Animations are ${AnimationsEnable?'enabled':'disabled'}`)
    if (AnimationsEnable) {
      for (var i = 0; i < animated_elements.length; i++) {
        animated_elements[i].classList.remove("no-transition");
      }
    } else {
      for (i = 0; i < animated_elements.length; i++) {
        animated_elements[i].classList.add("no-transition");
      }
    }
  }, [
    AnimationsEnable,
    FocusedHeatmapArea,
    CourseClusterExpansion,
    CourseClusterHasFocus,
    CourseEdges,
  ]);

  // Initialize the viewmodel for scale and centering
  useLayoutEffect(() => {
    let gel = document.querySelectorAll('[transform="matrix(1,0,0,1,0,0)"]');
    const heightOfNormalizedZoom = 2000;
    let tmp: any = {};
    tmp.a = window.innerHeight / heightOfNormalizedZoom;
    tmp.d = window.innerHeight / heightOfNormalizedZoom;
    tmp.e = window.innerWidth / 2;
    tmp.f = window.innerHeight / 2;
    tmp.b = 0;
    tmp.c = 0;
    if (gel[0] !== undefined) {
      gel[0].setAttribute(
        "transform",
        `matrix(${tmp.a},${tmp.b},${tmp.c},${tmp.d},${tmp.e},${tmp.f})`
      );
    }
    // ZoomToCentre();
    Viewer.current.reset(); //force update
  }, []);

  return (
    <div>
      <div className="relative" style={{ width: "100vw", height: "100vh" }}>
        <ReactSVGPanZoom
          ref={Viewer}
          // width={'100%'} height={'100%'}
          width={rwidth}
          height={rheight} //can normalize later?
          tool={tool}
          onChangeTool={setTool}
          value={value}
          onChangeValue={setValue}
          miniatureProps={{ position: "none" }}
          toolbarProps={{ position: "none" }}
          detectAutoPan={false}
          preventPanOutside={false}
          scaleFactorMax={1.5}
          scaleFactorMin={0.15}
        >
          <svg id="main-svg" width={rwidth} height={rheight}>
            <rect
              width={width * 10}
              height={height * 10}
              x={-width * 5}
              y={-height * 5}
              rx={0}
              fill={"snow"}
              id="zoom-bg"
              className="heatmap-bg-0"
              pointerEvents="none"
            />
            <PatternLines
              id={"bg-grid"}
              height={32}
              width={32}
              stroke="black"
              strokeWidth={0.1}
              orientation={["diagonal"]}
            />
            <PatternCircles
              id="bg-circles"
              height={64}
              width={64}
              stroke={"#00000000"}
              fill={"#00000044"}
              radius={2}
              complement={true}
              strokeWidth={1}
            />

            <Gradients />

            {/* On Safari, the circuitry background breaks and is replaced by a circle dot background */}
            {isSafari ? null : (
              <rect
                key="circle-bg"
                width={width * 10}
                height={height * 10}
                x={-width * 5}
                y={-height * 5}
                fill={`url('#circuit-bg')`}
              />
            )}
            <g>
              {/* Coursenode Clusters */}
              {FocusedHeatmapArea === undefined &&
                areas.map((area) => {
                  return (
                    <g
                      key={"area-cluster-key-" + area}
                      onClick={(e) => {
                        e.preventDefault();
                        toggleCourseCluster(area);
                      }}
                      onTouchCancel={(e) => {
                        e.preventDefault();
                        toggleCourseCluster(area);
                      }}
                      onTouchEnd={(e) => {
                        e.preventDefault();
                        toggleCourseCluster(area);
                      }}
                    >
                      <CourseNodeCluster
                        key={"cluster-" + area} // Add unique key here
                        area={area}
                        position={CourseClusterCenterArray[area]}
                        expanded={CourseClusterExpansion[area]}
                        // key={"cluster-" + area}
                        hasFocus={CourseClusterHasFocus[area]}
                        coreAngleArray={coreAngleArray}
                      />
                    </g>
                  );
                })}
              {/* Render Edges */}
              {CourseEdges.map((edge) => {
                return (
                  <g
                    key={`edge_from_${edge.sourceNodeRef.data.courseCode}_to_${edge.destNodeRef.data.courseCode}`}
                  >
                    <Connector
                      connType={edge.connType}
                      sourceNodeRef={edge.sourceNodeRef}
                      destNodeRef={edge.destNodeRef}
                      styleMode={edge.styleMode}
                    />
                  </g>
                );
              })}

              {/* Render Course Nodes */}
              {CourseNodes.map((el, idx) => {
                // console.log(idx);
                return (
                  <g
                    key={idx}
                    onClick={() => {
                      toggleCourseNode(el);
                    }}
                    className={
                      el.hidden ? "pointer-events-none" : "pointer-events-auto"
                    }
                    onTouchEnd={() => {
                      toggleCourseNode(el);
                    }}
                    onMouseEnter={(e) => {
                      setShowNodeTTip(true);
                      setNodeTTipLoc({
                        x: e.clientX && e.clientX,
                        y: e.clientY && e.clientY,
                      });
                      setNodeTTipString(GenerateCoursenodeTooltipString(el));
                    }}
                    onMouseLeave={() => {
                      setShowNodeTTip(false);
                      setNodeTTipLoc({ x: -1, y: -1 });
                      setNodeTTipString("");
                    }}
                  >
                    <CourseNode
                      // key={el.data.courseCode}
                      data={el.data}
                      position={el.position}
                      stylemode={el.stylemode}
                      hidden={el.hidden}
                      homeArea={el.homeArea}
                      relativePos={{
                        x: el.homeArea
                          ? el.position.x -
                          CourseClusterCenterArray[el.homeArea].x
                          : 0,
                        y: el.homeArea
                          ? el.position.y -
                          CourseClusterCenterArray[el.homeArea].y
                          : 0,
                      }}
                    />
                  </g>
                );
              })}
            </g>
          </svg>
        </ReactSVGPanZoom>
      </div>

      {/* Node Tooltip */}
      <div
        style={{
          left: nodeTTipLoc.x + 20,
          top: nodeTTipLoc.y + 20,
          position: "absolute",
        }}
        className={
          "bg-white text-black whitespace-pre-wrap  border border-black shadow-xl p-3 " +
          (showNodeTTip ? "opacity-90" : "opacity-0")
        }
        id="tooltip_item"
      >
        <pre className=" text-black whitespace-pre-wrap font-sans font-bold align-middle text-center">
          {" "}
          {nodeTTipString}
        </pre>
      </div>

      {/* Legend Panel */}
      <div>
        <Legend
          ZoomToArea={ZoomAndOpenArea}
          HeatmapOpen={FocusedHeatmapArea}
          HeatmapModalOpen={toggleModal}
          SetHeatmapFocus={SetFocusedHeatmapArea}
        ></Legend>
      </div>

      {/* Help Modal */}
      <HelpModal onClick={toggleHelpModal} />

      {/* Heatmap Modal */}
      <div
        className="modal w-full h-full top-0 left-0 flex fixed pointer-events-none opacity-0 items-center justify-center z-20"
        id="focus-area-selection-modal"
      >
        <div
          className="modal-background absolute w-full h-full bg-gray-900 opacity-40"
          onClick={toggleModal}
        ></div>
        <div
          className="modal-close cursor-pointer z-50 fixed top-4 right-4"
          onClick={toggleModal}
        >
          <svg
            className="fill-current text-white"
            xmlns="http://www.w3.org/2000/svg"
            width="18"
            height="18"
            viewBox="0 0 18 18"
          >
            <path d="M14.53 4.53l-1.06-1.06L9 7.94 4.53 3.47 3.47 4.53 7.94 9l-4.47 4.47 1.06 1.06L9 10.06l4.47 4.47 1.06-1.06L10.06 9z"></path>
          </svg>
        </div>
        <div className="modal-container bg-white w-120 px-6 py-3 rounded-xl shadow-2xl z-20 overflow-y-auto m-1">
          <p className=" rounded-t p-1 font-semibold text-center text-4xl">
            ECE Application Areas
          </p>
          <hr />
          <div className=" flex flex-row p-1">
            <p className="p-1 flex-grow">
              Select a card to see courses related to the application area.
            </p>
            <br />

            <p className="p-1">
              Current:{" "}
              <em>
                {FocusedHeatmapArea === undefined
                  ? "none"
                  : HeatmapAreas[FocusedHeatmapArea.areaIndex].areaName}
              </em>
            </p>

            {FocusedHeatmapArea === undefined ? null : (
              <button
                className="shadow-md border border-black rounded-md m-3 p-2 bg-red-400"
                onClick={() => SetFocusedHeatmapArea(undefined)}
              >
                Reset Heatmap
              </button>
            )}
          </div>
          {/* Want to block kb focus */}
          {ModalOpen && (
            <div className="grid grid-cols-2 gap-3 p-3">
              <button
                autoFocus
                className={
                  "border p-2 border-black rounded shadow-md flex flex-row justify-center gap-2 hover:bg-yellow-300" +
                  (FocusedHeatmapArea === HeatmapAreas[0]
                    ? " bg-green-300"
                    : "")
                }
                key={`hmac_${HeatmapAreas[0].areaName}`}
                style={{ transition: "background-color 200ms ease-in" }}
                onClick={() => {
                  SetFocusedHeatmapArea(HeatmapAreas[0]);
                  toggleModal();
                }}
              >
                <span className="icon is-small">
                  <i className={`fas fa-${HeatmapAreas[0].areaIcon}`}></i>
                </span>
                <span className="flex-grow">{HeatmapAreas[0].areaName}</span>
              </button>
              {HeatmapAreas.length > 0 ? (
                HeatmapAreas.slice(1).map((h_area) => {
                  return (
                    <button
                      className={
                        "border p-2 border-black rounded shadow-md flex flex-row justify-center gap-2 hover:bg-yellow-300" +
                        (FocusedHeatmapArea === h_area ? " bg-green-300" : "")
                      }
                      key={`hmac_${h_area.areaName}`}
                      style={{ transition: "background-color 200ms ease-in" }}
                      onClick={() => {
                        SetFocusedHeatmapArea(h_area);
                        //analytics testing
                        ReactGA.event({
                          category: "User",
                          action: `Set focused heatmap area to ${h_area.areaName}`,
                        });
                        toggleModal();
                      }}
                    >
                      <span className="icon is-small">
                        <i className={`fas fa-${h_area.areaIcon}`}></i>
                      </span>
                      <span className="flex-grow">{h_area.areaName}</span>
                    </button>
                  );
                })
              ) : (
                <p>Loading Heatmap Areas</p>
              )}
            </div>
          )}
        </div>
      </div>

      <div
        className="flex flex-col"
        style={{ position: "absolute", right: "1em", bottom: "1em" }}
      >
        {/* Control Panel */}
        <div className="flex flex-col py-3 place-self-end">
          <ControlButton
            customStyle=""
            tooltip="Zoom In"
            Label="plus"
            onClick={() => {
              Viewer.current?.zoomOnViewerCenter(1.4);
            }}
          ></ControlButton>
          <ControlButton
            customStyle=""
            tooltip="Zoom Out"
            Label="minus"
            onClick={() => {
              Viewer.current?.zoomOnViewerCenter(0.6);
            }}
          ></ControlButton>
          <ControlButton
            customStyle=""
            tooltip="Help"
            Label="question-circle"
            onClick={toggleHelpModal}
          ></ControlButton>
          <ControlButton
            customStyle=""
            tooltip="Center the View"
            Label="arrows-alt"
            onClick={() =>
              ZoomToCentre(FocusedHeatmapArea === undefined ? 1 : 0.6)
            }
          ></ControlButton>

          {/* {FocusedHeatmapArea !== undefined ? <ControlButton customStyle="" tooltip='Clear Heatmap' Label="times-circle" onClick={() => { SetFocusedHeatmapArea(undefined) }}></ControlButton> : null} */}
          {AnimationsEnable && (
            <ControlButton
              tooltip="Turn animations OFF"
              Label="pause-circle"
              customStyle=""
              onClick={() => {
                SetAnimationsEnable(false);
              }}
            />
          )}
          {!AnimationsEnable && (
            <ControlButton
              tooltip="Turn animations ON"
              Label="play-circle"
              customStyle=""
              onClick={() => {
                SetAnimationsEnable(true);
              }}
            />
          )}
        </div>
        {/* Search Bar */}
        <div className="flex flex-row">
          <button
            data-tooltip-content={"Search for a course!"}
            data-tooltip-id="tooltip-id-searchbar"
            onClick={() => {
              if (SearchBarVisible) {
                ZoomToCourse(
                  CourseNodes.find(
                    (el) =>
                      el.data.courseCode.toLocaleLowerCase() ===
                      SearchBarValue?.split(" - ")[0].toLocaleLowerCase()
                  )!
                );
              } else {
                SetSearchBarVisible(true);
              }
            }}
            className={
              "shadow-md bg-gray-50 bg-opacity-100 p-1 my-0 border-black border-l border-t border-b w-10 h-10 " +
              (SearchBarVisible ? "" : "border-r")
            }
          >
            <i className={"fa fa-search"}></i>
          </button>
          <Tooltip id="tooltip-id-searchbar" place="left"></Tooltip>
          {SearchBarVisible && (
            <>
              <button
                className="shadow-md bg-opacity-100 p-1 my-0 border-black border-l border-t border-b w-6 h-10 bg-red-300"
                data-tip="Hide Searchbar Input Field"
                data-place="top"
                onClick={() => SetSearchBarVisible(false)}
              >
                <i className={"fa fa-caret-right"}></i>
              </button>
              <input
                className="border border-black shadow-lg p-2 flex-grow searchbar h-10"
                type="text"
                placeholder={"Search for a Course"}
                list="course-search"
                style={{ fontFamily: "Rubik" }}
                autoComplete="off" // prevent previous entries from browser autocompl.
                onChange={(e) => {
                  SetSearchBarValue(e.target.value);
                }}
                onKeyDown={(e) => processSearch(e)}
              ></input>
              <datalist id="course-search">
                {SearchBarList?.map((el) => {
                  return <option key={"opt_key_" + el} value={el} />;
                })}
              </datalist>
            </>
          )}
        </div>
      </div>

      <ECELogo mode={FocusedHeatmapArea}></ECELogo>
    </div>
  );
};
